import { QRCodeSVG } from 'qrcode.react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorNotice } from '../common/ErrorNotice';
import { HeroImage } from '../elements/HeroImage';
import { LoadingOverlay } from '../elements/LoadingOverlay';
import type { API } from '@/api';
import { LoungeLogo } from '@/components/elements/LoungeLogo';
import { CANCEL_CUTOFF_MINUTES } from '@/constants/reservation';
import { ROUTE_PATH } from '@/constants/routes';
import { KEY_TYPE, SPACE_TYPE } from '@/constants/space';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { API_ENDPOINTS, useApiRequest } from '@/hooks/useApiRequest';
import { useCurrentTime } from '@/hooks/useCurrentTime';
import { useDateTimeFormat } from '@/hooks/useDateTimeFormat';
import { useDialog } from '@/hooks/useDialog';
import { useFloorFormat } from '@/hooks/useFloorFormat';
import { useNameFormat } from '@/hooks/useNameFormat';
import { getPersonalDeviceFloorMapImagePath } from '@/utils/floorMapPath';

type ReservationData =
  API['GET']['ReservationDetail']['response']['reservation'];

/**
 * 予約内容確認ページ
 */
export const ReservationDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatDateWithWeekday, formatTimeJst } = useDateTimeFormat();
  const { formatFloor } = useFloorFormat();
  const { formatName } = useNameFormat();
  const { translateApiError } = useApiErrorTranslation();

  const {
    dialogRef: mapDialogRef,
    open: openMapDialog,
    close: closeMapDialog,
  } = useDialog();
  const {
    dialogRef: cancelDialogRef,
    open: openCancelDialog,
    close: closeCancelDialog,
  } = useDialog();
  const {
    dialogRef: cancelCompleteDialogRef,
    open: openCancelCompleteDialog,
    close: closeCancelCompleteDialog,
  } = useDialog();

  const { execute: cancelReservation, isRequesting } = useApiRequest(
    'DELETE',
    API_ENDPOINTS.DELETE.CANCEL_RESERVATION,
    { showGlobalLoading: false },
  );

  const { execute: fetchReservationDetail } = useApiRequest(
    'GET',
    API_ENDPOINTS.GET.RESERVATION_DETAIL,
  );

  const [reservation, setReservation] = useState<ReservationData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 定期的に現在時刻を更新（キャンセル可否判定用）
  const now = useCurrentTime();

  // API呼び出し
  useEffect(() => {
    const fetchReservation = async () => {
      if (!id) {
        setError(t('reservationHistory.errors.reservationIdNotFound'));
        setLoading(false);
        return;
      }

      const result = await fetchReservationDetail({ id: Number(id) });

      if (result.ok) {
        setReservation(result.data.reservation);
      } else {
        setError(t('reservationHistory.errors.fetchFailed'));
      }
      setLoading(false);
    };
    fetchReservation();
  }, [id, fetchReservationDetail, t]);

  // 予約データがない場合は履歴一覧へ遷移
  useEffect(() => {
    if (!loading && !reservation) {
      navigate(ROUTE_PATH.RESERVATION_HISTORY);
    }
  }, [loading, reservation, navigate]);

  const handleCancelConfirm = async () => {
    if (!id) {
      setError(t('reservationHistory.errors.reservationIdNotFound'));
      closeCancelDialog();
      return;
    }

    // API呼び出し直前に再度キャンセル可否をチェック
    if (reservation) {
      const cutoffTime = new Date(
        new Date(reservation.start_time).getTime() -
          CANCEL_CUTOFF_MINUTES * 60 * 1000,
      );
      if (cutoffTime <= new Date()) {
        setError(t('reservationHistory.errors.cancelCutoffPassed'));
        closeCancelDialog();
        return;
      }
    }

    closeCancelDialog();

    const result = await cancelReservation({ id: Number(id) });

    if (result.ok) {
      openCancelCompleteDialog();
    } else {
      const { message } = translateApiError(result.error);
      setError(message);
    }
  };

  const handleCancelCompleteClose = () => {
    closeCancelCompleteDialog();
    navigate(
      `${ROUTE_PATH.RESERVATION_HISTORY}?type=${reservation?.space_type}`,
    );
  };

  // 読み込み中・エラー時の表示
  if (loading) return <div>{t('common.loading')}</div>;
  if (!reservation) return null; // useEffectで履歴一覧へ遷移

  // フロアマップ画像パスを生成
  const floorMapImagePath = getPersonalDeviceFloorMapImagePath(reservation);

  // 利用時間（分）を計算
  const calculateDuration = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.round((endDate.getTime() - startDate.getTime()) / (1000 * 60));
  };

  const duration = calculateDuration(
    reservation.start_time,
    reservation.end_time,
  );

  // サービスタイプに応じたクラス
  const boxClass =
    reservation.space_type === SPACE_TYPE.SHOWER_ROOM
      ? 'l-box--shower'
      : 'l-box--booth';

  // シャワールームかブースか
  const isShowerRoom = reservation.space_type === SPACE_TYPE.SHOWER_ROOM;
  const isPrivateRoom = reservation.space_type === SPACE_TYPE.PRIVATE_ROOM;

  // passcodeの有無
  const hasPasscode = reservation.pass_code !== null;

  // キータイプ（デジタルか物理キーか）
  const isDigitalKey = reservation.key_type === KEY_TYPE.DIGITAL;
  const isPhysicalKey = reservation.key_type === KEY_TYPE.PHYSICAL_KEY_ONLY;

  // 予約開始N分前からキャンセル不可
  const cutoffTime = new Date(
    new Date(reservation.start_time).getTime() -
      CANCEL_CUTOFF_MINUTES * 60 * 1000,
  );
  const isCancelDisabled = cutoffTime <= now;

  return (
    <div className="page p-auth page_c1">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                {/* ヒーロー */}
                <HeroImage src="/Common_Title.jpg" alt="LOUNGE RESERVATION" />

                {/* エラー表示 */}
                {error && <ErrorNotice message={error} />}

                {/* エリア群（グリッド） */}
                <div className="l-grid">
                  {/* ボックス1：サービス */}
                  <section className="l-card reservation-card">
                    <div className="l-card__body reservation-card__body">
                      {/* 予約詳細エリア */}
                      <div className={`l-box ${boxClass} reservation-detail`}>
                        {/* 予約情報リスト */}
                        <div className="reservation-detail__section reservation-detail__list">
                          {/* 氏名 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__name">
                              {formatName(
                                reservation.last_name,
                                reservation.first_name,
                              )}
                            </div>
                          </div>

                          {/* 日付 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__date">
                              {formatDateWithWeekday(reservation.start_time)}
                            </div>
                          </div>

                          {/* 時間 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__time">
                              {formatTimeJst(reservation.start_time)}
                              {t('reservation.confirm.timeRange')}
                              {formatTimeJst(reservation.end_time)}
                              {t('reservation.confirm.durationFormat', {
                                duration,
                              })}
                            </div>
                          </div>
                          {/* タイムゾーン注記 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-card__info reservation-card__info--timezone-left">
                              {t('reservation.boarding.timezoneNote')}
                            </div>
                          </div>

                          {/* 空港 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__airport">
                              {t(`common.airport.${reservation.airport}`)}
                              <button
                                className="reservation-detail__btn reservation-detail__btn--map js-open-dialog"
                                type="button"
                                onClick={openMapDialog}
                              >
                                MAP
                              </button>
                            </div>
                          </div>

                          {/* ターミナル */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__desc reservation-detail__terminal">
                              {t(`common.terminal.${reservation.terminal}`)}{' '}
                              {t(
                                `common.flightType.${reservation.flight_type}`,
                              )}
                            </div>
                          </div>

                          {/* ラウンジ */}
                          <div className="reservation-detail__row reservation-detail__row--lounge">
                            <div className="reservation-detail__desc reservation-detail__lounge">
                              <div className="reservation-detail__lounge-inner">
                                <LoungeLogo
                                  lounge={reservation.lounge}
                                  className="reservation-detail__lounge-logo"
                                  altText={t(
                                    `common.lounge.${reservation.lounge}`,
                                  )}
                                />
                                <div className="reservation-detail__lounge-floor">
                                  {formatFloor(reservation.floor)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* 予約番号 */}
                          <div className="reservation-detail__row">
                            <div className="reservation-detail__term">
                              {t('reservation.complete.reservationNumber')}
                            </div>
                            <div className="reservation-detail__desc reservation-detail__reservation">
                              <span className="reservation-detail__code">
                                {reservation.reservation_no}
                              </span>
                            </div>
                          </div>

                          {/* 部屋 */}
                          <div className="reservation-detail__row reservation-detail__row--room">
                            <div className="reservation-detail__desc reservation-detail__room">
                              <div className="reservation-detail__room-inner">
                                <div className="reservation-detail__room-img">
                                  <img
                                    className="reservation-detail__room-icon"
                                    src={
                                      isShowerRoom
                                        ? '/Common_Reservation_Shower.png'
                                        : '/Common_Reservation_Booth.png'
                                    }
                                    alt={
                                      isShowerRoom
                                        ? t('reservation.serviceType.shower')
                                        : t('reservation.serviceType.booth')
                                    }
                                  />
                                </div>
                                <div className="reservation-detail__room-no">
                                  No,
                                  <span className="reservation-detail__room-no-number">
                                    {reservation.space_no}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* シャワールーム + デジタルキー(パスコード有り)の場合：パスコード表示 */}
                        {isShowerRoom && isDigitalKey && (
                          <>
                            {/* パスコードの表示 */}
                            {hasPasscode && (
                              <div className="reservation-detail__section reservation-detail__section--passcode">
                                <div className="reservation-detail__section-title">
                                  {t('reservationHistory.detail.passCode')}
                                </div>
                                <div className="reservation-detail__passcode">
                                  {reservation.pass_code
                                    .split('')
                                    .map((digit, index) => (
                                      <div
                                        key={index}
                                        className="reservation-detail__passcode-digit"
                                      >
                                        {digit}
                                      </div>
                                    ))}
                                </div>
                              </div>
                            )}

                            {/* 解除用パスコードが無い場合 */}
                            {!hasPasscode && (
                              <ErrorNotice
                                message={t(
                                  'reservationHistory.detail.digitalKey.noPasscodeNotice',
                                )}
                              />
                            )}

                            {/* 入室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--enter">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.digitalKey.entry.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.entry.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* 入室ガイド画像 */}
                            <div className="reservation-detail__section reservation-detail__section--image">
                              <img
                                className="reservation-detail__image reservation-detail__image--guide-1"
                                src={`/C_2_Image00_${i18n.language === 'en' ? 'EN' : 'JP'}.png`}
                                alt={t('common.altText.entryGuide')}
                              />
                            </div>

                            <div className="reservation-detail__section reservation-detail__section--image">
                              <img
                                className="reservation-detail__image reservation-detail__image--guide-2"
                                src={`/C_2_Image01_${i18n.language === 'en' ? 'EN' : 'JP'}.png`}
                                alt={t('common.altText.entryGuideStep2')}
                              />
                            </div>

                            {/* 退室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.digitalKey.exit.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.exit.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.exit.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* ご利用上の注意 */}
                            <div className="reservation-detail__section reservation-detail__section--note">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.digitalKey.notice.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.notice.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.notice.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.digitalKey.notice.line3',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* シャワールーム + 物理キー or passcodeを持っていない場合 */}
                        {isShowerRoom && isPhysicalKey && (
                          <>
                            {/* 入室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--enter">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.physicalKey.entry.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.entry.line1',
                                    )}
                                  </div>
                                </div>
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.entry.line3',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* 退室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.physicalKey.exit.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.exit.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.exit.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* ご利用上の注意 */}
                            <div className="reservation-detail__section reservation-detail__section--note">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.physicalKey.notice.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.notice.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.notice.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.physicalKey.notice.line3',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}

                        {/* 防音個室ブースの場合：QRコード表示 */}
                        {isPrivateRoom && (
                          <>
                            {/* 解除用QRコードセクション */}
                            {hasPasscode && (
                              <>
                                <div className="reservation-detail__section reservation-detail__section--enter">
                                  <div className="reservation-detail__section-title">
                                    {t(
                                      'reservationHistory.detail.privateRoom.qrCodeLabel',
                                    )}
                                  </div>
                                </div>
                                <div className="reservation-detail__section reservation-detail__section--image">
                                  <div className="reservation-detail__qrcode">
                                    <QRCodeSVG
                                      value={reservation.pass_code}
                                      size={180}
                                    />
                                  </div>
                                </div>
                              </>
                            )}

                            {/* 解除用QRに使うパスコードが無い場合 */}
                            {!hasPasscode && (
                              <ErrorNotice
                                message={t(
                                  'reservationHistory.detail.privateRoom.noPasscodeNotice',
                                )}
                              />
                            )}

                            {/* 入室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.privateRoom.entry.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.entry.line1',
                                    )}
                                    {t(
                                      'reservationHistory.detail.privateRoom.entry.line2',
                                    ) && (
                                      <>
                                        <br />
                                        {t(
                                          'reservationHistory.detail.privateRoom.entry.line2',
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.entry.line3',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* 退室方法 */}
                            <div className="reservation-detail__section reservation-detail__section--exit">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.privateRoom.exit.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.exit.line1',
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.exit.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* ご利用上の注意 */}
                            <div className="reservation-detail__section reservation-detail__section--note">
                              <div className="reservation-detail__section-title">
                                {t(
                                  'reservationHistory.detail.privateRoom.notice.title',
                                )}
                              </div>
                              <div className="reservation-detail__section-body">
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.notice.line1',
                                    )}
                                  </div>
                                </div>
                                <div className="reservation-detail__text-row">
                                  <div className="reservation-detail__text-marker">
                                    ・
                                  </div>
                                  <div className="reservation-detail__text">
                                    {t(
                                      'reservationHistory.detail.privateRoom.notice.line2',
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      {/* アクションエリア */}
                      <div className="reservation-card__actions reservation-card__actions--open">
                        <div className="reservation-card__actions-item">
                          <button
                            className="reservation-card__action-link reservation-card__action-link--cancel js-open-dialog"
                            type="button"
                            onClick={openCancelDialog}
                            disabled={isCancelDisabled || isRequesting}
                          >
                            {isRequesting
                              ? t('reservation.complete.canceling')
                              : t('reservation.complete.cancelButton')}
                          </button>
                          <div className="reservation-card__note">
                            {t('reservation.complete.cancelNote')}
                          </div>
                        </div>
                        <div className="reservation-card__actions-item">
                          <button
                            className="reservation-card__action-link reservation-card__action-link--cancel js-open-dialog"
                            type="button"
                            onClick={() =>
                              navigate(
                                `${ROUTE_PATH.RESERVATION_HISTORY}?type=${reservation.space_type}`,
                              )
                            }
                            disabled={isRequesting}
                          >
                            {t('common.back')}
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>

      {/* マップダイアログ */}
      <dialog className="dialog dialog--map" ref={mapDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <button
              className="dialog__btn--close js-close-dialog"
              type="button"
              onClick={closeMapDialog}
            >
              ×
            </button>
            <div className="dialog__map-image">
              {floorMapImagePath && (
                <img
                  src={floorMapImagePath}
                  alt={`${t(`common.terminal.${reservation.terminal}`)} ${t(`common.airport.${reservation.airport}`)} ${t(`common.lounge.${reservation.lounge}`)}`}
                />
              )}
            </div>
          </div>
        </div>
      </dialog>

      {/* キャンセル確認ダイアログ */}
      <dialog className="dialog dialog--cancel" ref={cancelDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <div className="dialog__text">
              <p className="dialog__text-line">
                {t('reservation.complete.cancelDialog.message')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </p>
            </div>
            <div className="dialog__actions">
              <button
                className="dialog__btn dialog__btn--yes js-bt-dialog"
                type="button"
                onClick={handleCancelConfirm}
              >
                {t('reservation.complete.cancelDialog.confirmButton')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </button>
              <button
                className="dialog__btn dialog__btn--no js-bt-dialog"
                type="button"
                onClick={closeCancelDialog}
              >
                {t('reservation.complete.cancelDialog.cancelButton')
                  .split('\n')
                  .map((line, i) => (
                    <span key={i}>
                      {line}
                      {i === 0 && <br />}
                    </span>
                  ))}
              </button>
            </div>
          </div>
        </div>
      </dialog>

      {/* ローディングオーバーレイ */}
      <LoadingOverlay
        open={isRequesting}
        message={t('reservation.complete.canceling')}
      />

      {/* キャンセル完了ダイアログ */}
      <dialog className="dialog dialog--cancel02" ref={cancelCompleteDialogRef}>
        <div className="dialog__line">
          <div className="dialog__area">
            <div className="dialog__text">
              <p className="dialog__text-line">
                {t('reservation.complete.cancelCompleteDialog.message')}
              </p>
            </div>
            <div className="dialog__actions">
              <button
                className="dialog__btn dialog__btn--history js-bt-dialog"
                type="button"
                onClick={handleCancelCompleteClose}
              >
                {t('reservationHistory.backToHistory')}
              </button>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  );
};
